import {canonicalizeLanguageCode} from "@/lib/LanguageSupport";

export const EMBED_LANGUAGE_PARAM = "lang";
export const EMBED_LANGUAGE_ALIAS_PARAM = "preferred_language";

export function normalizeEmbedPreferredLanguage(value: unknown): string | undefined {
    if (typeof value !== "string") return undefined;
    const input = value.trim().replace(/_/g, "-");
    if (!input || input.length > 64) return undefined;

    try {
        const canonical = new Intl.Locale(input).toString();
        const knownCode = canonicalizeLanguageCode(canonical);
        // Preserve Intl's script/region casing for catalog-unknown but valid tags.
        return knownCode.toLowerCase() === canonical.toLowerCase() ? canonical : knownCode;
    } catch {
        return undefined;
    }
}

export function shouldApplyEmbedPreferredLanguage(
    preferredLanguage: string | undefined,
    appliedLanguage: string,
): boolean {
    return Boolean(preferredLanguage && preferredLanguage !== appliedLanguage);
}

export function getEmbedPreferredLanguage(search: string | URLSearchParams): string | undefined {
    const params = typeof search === "string"
        ? new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)
        : search;
    const primary = normalizeEmbedPreferredLanguage(params.get(EMBED_LANGUAGE_PARAM));
    if (primary) return primary;
    return normalizeEmbedPreferredLanguage(params.get(EMBED_LANGUAGE_ALIAS_PARAM));
}

/** Append `?lang=` only when the selected language differs from the source. */
export function resolveEmbedCodeLanguageParam(
    selectedLanguage: string | undefined,
    sourceLanguageCode: string | undefined,
): string | undefined {
    const selected = normalizeEmbedPreferredLanguage(selectedLanguage);
    if (!selected) return undefined;

    const source = normalizeEmbedPreferredLanguage(sourceLanguageCode)
        || canonicalizeLanguageCode(sourceLanguageCode)
        || undefined;
    if (!source) return selected;

    if (selected.toLowerCase() === source.toLowerCase()) return undefined;
    return selected;
}

/** Build an embed player URL, optionally forcing a startup language. */
export function withEmbedPreferredLanguage(
    url: string,
    preferredLanguage?: string,
): string {
    const lang = normalizeEmbedPreferredLanguage(preferredLanguage);
    if (!lang) return url;
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${EMBED_LANGUAGE_PARAM}=${encodeURIComponent(lang)}`;
}
